
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import {
  AuthGate,
  SignInInputSchema,
  authPost,
  clearStashedGrant,
  invalidateSession,
} from '@client/shared/auth/index.ts';

async function postLogin(email: string, password: string): Promise<{ ok: boolean; error?: string }> {
  const response = await authPost('/auth/login', 'login', { email, password });
  if (response.ok) return { ok: true };
  let error = 'Couldn\'t sign in. Try again.';
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string') error = body.error;
  } catch {
  }
  return { ok: false, error };
}

export function SignIn() {
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { email: '', password: '' },
    validators: { onSubmit: zodValidator(SignInInputSchema) },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const result = await postLogin(value.email.trim(), value.password);
      if (!result.ok) {
        setFormError(result.error ?? 'Couldn\'t sign in. Try again.');
        return;
      }
      clearStashedGrant();
      await invalidateSession(queryClient);
    },
  });

  return (
    <AuthGate
      eyebrow="Open house scheduling"
      heading="Welcome back."
      intro={
        <>
          New to Cheddule? <a href="/signup">Start free.</a>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        noValidate
      >
        <Stack gap={4}>
          <form.Field name="email">
            {(field) => (
              <TextField
                field={field}
                label="Email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@yourgroup.com"
              />
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <TextField
                field={field}
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
              />
            )}
          </form.Field>

          {formError && (
            <Text size="sm" tone="danger" role="alert">
              {formError}
            </Text>
          )}

          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" block loading={isSubmitting}>
                Sign in
              </Button>
            )}
          </form.Subscribe>

          <Text size="sm" tone="soft">
            <a href="/forgot-password">Forgot your password?</a>
          </Text>
        </Stack>
      </form>
    </AuthGate>
  );
}
